import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send, CheckCircle, Mail, User, MessageSquare, AlertCircle, Phone } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactForm = () => {
  const { language } = useLanguage();
  const { ref, isVisible } = useScrollAnimation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow numbers and limit to 11 digits
      const cleaned = value.replace(/\D/g, '').slice(0, 11);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      
      // Validate phone
      if (cleaned.length > 0 && !cleaned.startsWith('05')) {
        setPhoneError(language === 'tr' ? 'Telefon numarası 05 ile başlamalıdır' : 'Phone number must start with 05');
      } else if (cleaned.length > 0 && cleaned.length !== 11) {
        setPhoneError(language === 'tr' ? 'Telefon numarası 11 haneli olmalıdır' : 'Phone number must be 11 digits');
      } else {
        setPhoneError('');
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const formatPhoneDisplay = (phone) => {
    if (phone.length <= 4) return phone;
    if (phone.length <= 7) return `${phone.slice(0, 4)} ${phone.slice(4)}`;
    return `${phone.slice(0, 4)} ${phone.slice(4, 7)} ${phone.slice(7)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone before submit
    if (!formData.phone.startsWith('05') || formData.phone.length !== 11) {
      setPhoneError(language === 'tr' ? 'Geçerli bir telefon numarası girin (05XX XXX XXXX)' : 'Enter a valid phone number (05XX XXX XXXX)');
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Bir hata oluştu');
      }

      setIsSubmitted(true);
      
      // Reset after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', phone: '', email: '', message: '' });
      }, 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact"
      ref={ref}
      className="py-24 md:py-32 bg-slate-50 dark:bg-slate-950 theme-transition relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className={`${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
            <h2 className="section-title mb-4" data-testid="contact-title">
              {translations.contact.title[language]}
            </h2>
            <p className="section-subtitle mb-8">
              {translations.contact.subtitle[language]}
            </p>

            {/* Contact Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-indigo-600 dark:text-indigo-400" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-500">
                    {language === 'tr' ? 'E-posta' : 'Email'}
                  </div>
                  <a 
                    href="mailto:info@agrostech.com" 
                    className="text-slate-900 dark:text-white font-medium hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    info@agrostech.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div 
            className={`glass-card ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.1s' }}
          >
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {translations.contact.success[language]}
                </h3>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <User className="w-4 h-4" strokeWidth={1.5} />
                    {translations.contact.name[language]}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={translations.contact.namePlaceholder[language]}
                    required
                    className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-indigo-500"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Phone Field */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                    {language === 'tr' ? 'Telefon' : 'Phone'} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formatPhoneDisplay(formData.phone)}
                    onChange={handleChange}
                    placeholder="05XX XXX XXXX"
                    required
                    className={`bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-indigo-500 ${phoneError ? 'border-red-500 dark:border-red-500' : ''}`}
                    data-testid="contact-phone-input"
                  />
                  {phoneError && (
                    <p className="text-xs text-red-500 mt-1">{phoneError}</p>
                  )}
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                    {translations.contact.email[language]}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={translations.contact.emailPlaceholder[language]}
                    required
                    className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-indigo-500"
                    data-testid="contact-email-input"
                  />
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                    {translations.contact.message[language]}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={translations.contact.messagePlaceholder[language]}
                    required
                    rows={4}
                    className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 focus:ring-indigo-500 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {translations.contact.submit[language]}
                      <Send className="w-4 h-4" strokeWidth={1.5} />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
